// ./src/hooks/useEmbeddingEventTemplates.ts
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";
import {
  EmbeddingEventTemplate,
  EmbeddingEventTemplateService,
  EmbeddingEventTemplateFilter,
  EmbeddingEventTemplateInput,
  Pagination,
} from "@/utils/api";
import { SortConfig } from "@/components/generic";
import { DEFAULT_PAGE_SIZE } from "@/config";

export interface UseEmbeddingEventTemplatesOptions {
  /** Initial page size for pagination */
  pageSize?: number;

  /** Initial filters */
  initialFilters?: Partial<EmbeddingEventTemplateFilter>;

  /** Initial sort configuration */
  initialSort?: SortConfig[];

  /** Auto-load data on mount */
  autoLoad?: boolean;

  /** Authentication status */
  isAuthenticated?: boolean;

  userId?: string | null;
}

export interface UseEmbeddingEventTemplatesReturn {
  // Data state
  embeddingEventTemplate: EmbeddingEventTemplate | null;
  embeddingEventTemplates: EmbeddingEventTemplate[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  isEmpty: boolean;

  // Search and filters
  filters: Partial<EmbeddingEventTemplateFilter>;
  sortBy: SortConfig[];

  // Pagination
  currentPage: number;
  pageSize: number;

  // Operations state
  isCreating: boolean;
  isUpdating: boolean;
  removingTemplateId: string | null;
  selectedTemplate: EmbeddingEventTemplate | null;

  // Core operations (matching API method names exactly)
  get: (id: string) => Promise<void>;
  list: (
    filter?: Partial<EmbeddingEventTemplateFilter>,
    pagination?: Pagination,
    sortBy?: SortConfig[],
    reset?: boolean
  ) => Promise<void>;
  getMany: (ids: string[]) => Promise<EmbeddingEventTemplate[]>;
  create: (
    params: EmbeddingEventTemplateInput
  ) => Promise<EmbeddingEventTemplate | null>;
  update: (
    id: string,
    params: EmbeddingEventTemplateInput
  ) => Promise<EmbeddingEventTemplate | null>;
  remove: (id: string) => Promise<boolean>;
  removeMany: (ids: string[]) => Promise<boolean>;

  // Additional convenience methods
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;

  // Search and filter operations
  setFilters: (filters: Partial<EmbeddingEventTemplateFilter>) => void;
  setSortBy: (sort: SortConfig[]) => void;

  // UI state management
  setSelectedTemplate: (template: EmbeddingEventTemplate | null) => void;

  // Helper operations
  reset: () => void;

  // Computed values
  templatesByStatus: Record<string, EmbeddingEventTemplate[]>;
  templatesByLayoutType: Record<string, EmbeddingEventTemplate[]>;

  // Error handling
  lastError: string | null;
  clearError: () => void;
}

const DEFAULT_SORT: SortConfig[] = [{ key: "name", direction: "asc" }];

export const useEmbeddingEventTemplates = (
  options: UseEmbeddingEventTemplatesOptions = {}
): UseEmbeddingEventTemplatesReturn => {
  const {
    pageSize = DEFAULT_PAGE_SIZE,
    initialFilters = {},
    initialSort = DEFAULT_SORT,
    autoLoad = true,
    isAuthenticated = false,
  } = options;

  // Use refs for option values to avoid dependency issues
  const pageSizeRef = useRef(pageSize);
  const sortByRef = useRef<SortConfig[]>(initialSort);

  // Update refs when options change
  useEffect(() => {
    pageSizeRef.current = pageSize;
  }, [pageSize]);

  // Data state
  const [embeddingEventTemplate, setEmbeddingEventTemplate] =
    useState<EmbeddingEventTemplate | null>(null);
  const [embeddingEventTemplates, setEmbeddingEventTemplates] = useState<
    EmbeddingEventTemplate[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  // Search and filters
  const [filters, setFilters] =
    useState<Partial<EmbeddingEventTemplateFilter>>(initialFilters);
  const [sortBy, setSortBy] = useState<SortConfig[]>(initialSort);

  // Update sortByRef when sortBy state changes
  useEffect(() => {
    sortByRef.current = sortBy;
  }, [sortBy]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Operations state
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [removingTemplateId, setRemovingTemplateId] = useState<string | null>(
    null
  );
  const [selectedTemplate, setSelectedTemplate] =
    useState<EmbeddingEventTemplate | null>(null);

  // Error handling
  const [lastError, setLastError] = useState<string | null>(null);

  // Computed values
  const isEmpty = useMemo(
    () => embeddingEventTemplates.length === 0 && !loading,
    [embeddingEventTemplates.length, loading]
  );

  const templatesByStatus = useMemo(() => {
    return embeddingEventTemplates.reduce((acc, template) => {
      const status = String(template.status || "unknown");
      if (!acc[status]) {
        acc[status] = [];
      }
      acc[status].push(template);
      return acc;
    }, {} as Record<string, EmbeddingEventTemplate[]>);
  }, [embeddingEventTemplates]);

  const templatesByLayoutType = useMemo(() => {
    return embeddingEventTemplates.reduce((acc, template) => {
      const layoutType = template.layoutType || "unknown";
      if (!acc[layoutType]) {
        acc[layoutType] = [];
      }
      acc[layoutType].push(template);
      return acc;
    }, {} as Record<string, EmbeddingEventTemplate[]>);
  }, [embeddingEventTemplates]);

  // Helper function to update template in list if it exists, or add it if not
  const updateOrAddTemplateInList = useCallback(
    (updatedTemplate: EmbeddingEventTemplate) => {
      setEmbeddingEventTemplates((prev) => {
        const existingIndex = prev.findIndex(
          (t) => t.id === updatedTemplate.id
        );
        if (existingIndex >= 0) {
          const newTemplates = [...prev];
          newTemplates[existingIndex] = updatedTemplate;
          return newTemplates;
        } else {
          return [updatedTemplate, ...prev];
        }
      });
    },
    []
  );

  // Helper function to remove template from list if it exists
  const removeTemplateFromList = useCallback((templateId: string) => {
    setEmbeddingEventTemplates((prev) =>
      prev.filter((t) => t.id !== templateId)
    );
  }, []);

  // Helper function to remove multiple templates from list
  const removeTemplatesFromList = useCallback((templateIds: string[]) => {
    const idsSet = new Set(templateIds);
    setEmbeddingEventTemplates((prev) =>
      prev.filter((t) => !idsSet.has(t.id || ""))
    );
  }, []);

  // Clear error function
  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  // Reset function
  const reset = useCallback(() => {
    setEmbeddingEventTemplate(null);
    setEmbeddingEventTemplates([]);
    setLastError(null);
    setLoading(false);
    setLoadingMore(false);
    setHasMore(false);
    setCurrentPage(1);
    setIsCreating(false);
    setIsUpdating(false);
    setRemovingTemplateId(null);
    setSelectedTemplate(null);
  }, []);

  // Get single template (matching API method name)
  const get = useCallback(
    async (id: string) => {
      setLoading(true);
      setLastError(null);

      try {
        const result = await EmbeddingEventTemplateService.get(id);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const templateData = result.data[0] as EmbeddingEventTemplate;
          setEmbeddingEventTemplate(templateData);
          updateOrAddTemplateInList(templateData);
          setLastError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load template";
          setLastError(errorMessage);
          setEmbeddingEventTemplate(null);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to load template";
        setLastError(errorMessage);
        setEmbeddingEventTemplate(null);
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddTemplateInList]
  );

  // List templates (matching API method name) - NO dependencies
  const list = useCallback(
    async (
      filter: Partial<EmbeddingEventTemplateFilter> = {},
      pagination?: Pagination,
      sortByParams?: SortConfig[],
      reset: boolean = false
    ) => {
      const targetPage = pagination?.page || 1;
      const targetPageSize = pagination?.pageSize || pageSizeRef.current;
      const isAppending = !reset;

      if (isAppending) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        if (reset) {
          setEmbeddingEventTemplates([]);
          setCurrentPage(1);
        }
      }

      setFilters(filter);
      if (sortByParams) setSortBy(sortByParams);

      const currentSortBy = sortByParams || sortByRef.current;

      try {
        const paginationParams: Pagination = {
          page: targetPage,
          pageSize: targetPageSize,
        };

        const sortParams = currentSortBy.map((sort) => ({
          name: sort.key,
          order: sort.direction.toUpperCase() as "ASC" | "DESC",
        }));

        const result = await EmbeddingEventTemplateService.list(
          filter as EmbeddingEventTemplateFilter,
          paginationParams,
          sortParams
        );

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newTemplates = Array.isArray(result.data) ? result.data : [];

          if (isAppending) {
            setEmbeddingEventTemplates((prev) => {
              const existingIds = new Set(prev.map((t) => t.id));
              const uniqueNewTemplates = newTemplates.filter(
                (t: any) => !existingIds.has(t.id)
              );
              return [...prev, ...uniqueNewTemplates];
            });
          } else {
            setEmbeddingEventTemplates(newTemplates);
          }

          setHasMore(newTemplates.length === targetPageSize);
          setCurrentPage(targetPage);
          setLastError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load templates";
          setLastError(errorMessage);
          if (!isAppending) setEmbeddingEventTemplates([]);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to load templates";
        setLastError(errorMessage);
        if (!isAppending) setEmbeddingEventTemplates([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    []
  );

  // Get many templates by IDs (matching API method name)
  const getMany = useCallback(
    async (ids: string[]): Promise<EmbeddingEventTemplate[]> => {
      if (ids.length === 0) {
        return [];
      }

      setLoading(true);
      setLastError(null);

      try {
        const result = await EmbeddingEventTemplateService.getMany(ids);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const loadedTemplates = Array.isArray(result.data) ? result.data : [];
          loadedTemplates.forEach((template: any) => {
            updateOrAddTemplateInList(template);
          });
          setLastError(null);
          return loadedTemplates;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load templates";
          setLastError(errorMessage);
          return [];
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to load templates";
        setLastError(errorMessage);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddTemplateInList]
  );

  // Create template (matching API method name)
  const create = useCallback(
    async (
      params: EmbeddingEventTemplateInput
    ): Promise<EmbeddingEventTemplate | null> => {
      setIsCreating(true);
      setLastError(null);

      try {
        const result = await EmbeddingEventTemplateService.create(params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newTemplate = result.data[0] as EmbeddingEventTemplate;
          setEmbeddingEventTemplate(newTemplate);
          setEmbeddingEventTemplates((prev) => [newTemplate, ...prev]);
          setLastError(null);
          return newTemplate;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to create template";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to create template";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsCreating(false);
      }
    },
    []
  );

  // Update template (matching API method name)
  const update = useCallback(
    async (
      id: string,
      params: EmbeddingEventTemplateInput
    ): Promise<EmbeddingEventTemplate | null> => {
      if (!id) {
        setLastError("Template ID is required for updating");
        return null;
      }

      setIsUpdating(true);
      setLastError(null);

      try {
        const result = await EmbeddingEventTemplateService.update(
          { id },
          params
        );

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const updatedTemplate = result.data[0] as EmbeddingEventTemplate;
          setEmbeddingEventTemplate(updatedTemplate);
          updateOrAddTemplateInList(updatedTemplate);
          setLastError(null);
          return updatedTemplate;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to update template";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to update template";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsUpdating(false);
      }
    },
    [updateOrAddTemplateInList]
  );

  // Remove template (matching API method name)
  const remove = useCallback(
    async (id: string): Promise<boolean> => {
      if (!id) {
        setLastError("Template ID is required for removal");
        return false;
      }

      setRemovingTemplateId(id);
      setLastError(null);

      try {
        const result = await EmbeddingEventTemplateService.remove(id);

        if (result.code === OP_RESULT_CODES.OK) {
          setEmbeddingEventTemplate(null);
          removeTemplateFromList(id);
          setLastError(null);
          return true;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to remove template";
          setLastError(errorMessage);
          return false;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to remove template";
        setLastError(errorMessage);
        return false;
      } finally {
        setRemovingTemplateId(null);
      }
    },
    [removeTemplateFromList]
  );

  // Remove many templates (matching API method name)
  const removeMany = useCallback(
    async (ids: string[]): Promise<boolean> => {
      if (ids.length === 0) {
        setLastError("At least one Template ID is required for removal");
        return false;
      }

      setLastError(null);

      try {
        const result = await EmbeddingEventTemplateService.removeMany(ids);

        if (result.code === OP_RESULT_CODES.OK) {
          setEmbeddingEventTemplate(null);
          removeTemplatesFromList(ids);
          setLastError(null);
          return true;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to remove templates";
          setLastError(errorMessage);
          return false;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to remove templates";
        setLastError(errorMessage);
        return false;
      }
    },
    [removeTemplatesFromList]
  );

  // Load more templates
  const loadMore = useCallback(async () => {
    if (!loadingMore && hasMore) {
      const nextPage = currentPage + 1;
      await list(
        filters,
        { page: nextPage, pageSize: pageSizeRef.current },
        sortByRef.current,
        false
      );
    }
  }, [list, loadingMore, hasMore, currentPage, filters]);

  // Refresh templates
  const refresh = useCallback(async () => {
    await list(
      filters,
      { page: 1, pageSize: pageSizeRef.current },
      sortByRef.current,
      true
    );
  }, [list, filters]);

  // Auto-load effect
  const [hasAutoLoaded, setHasAutoLoaded] = useState(false);

  useEffect(() => {
    if (autoLoad && isAuthenticated && !hasAutoLoaded) {
      setHasAutoLoaded(true);
      list(initialFilters, { page: 1, pageSize }, initialSort, true);
    }
  }, [autoLoad, isAuthenticated, hasAutoLoaded, list, pageSize, initialSort]);

  return {
    embeddingEventTemplate,
    embeddingEventTemplates,
    loading,
    loadingMore,
    hasMore,
    isEmpty,
    filters,
    sortBy,
    currentPage,
    pageSize,
    isCreating,
    isUpdating,
    removingTemplateId,
    selectedTemplate,
    get,
    list,
    getMany,
    create,
    update,
    remove,
    removeMany,
    loadMore,
    refresh,
    setFilters,
    setSortBy,
    setSelectedTemplate,
    reset,
    templatesByStatus,
    templatesByLayoutType,
    lastError,
    clearError,
  };
};
